import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "bcpl_pwa_install_dismissed";

export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onInstallable = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    const mq = window.matchMedia("(display-mode: standalone)");
    if (mq.matches || window.navigator.standalone) {
      setInstalled(true);
    }

    window.addEventListener("beforeinstallprompt", onInstallable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onInstallable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
    setDeferred(null);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setDeferred(null);
    }
  };

  if (dismissed || installed || !deferred) return null;

  return (
    <div
      className="fixed left-4 right-4 z-50 mx-auto max-w-md rounded-2xl border border-bansal-sage/30 bg-card p-4 shadow-xl bottom-[calc(4.5rem+env(safe-area-inset-bottom))] lg:bottom-4 sm:left-auto sm:right-4"
      role="region"
      aria-label="Install app"
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-bansal-sage/15 text-primary">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">Install Bansal ERP</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Add to your home screen for faster access on the plant floor.
          </p>
          <div className="mt-3 flex gap-2">
            <Button type="button" size="sm" className="bg-bansal-sage hover:bg-bansal-sageDark text-white" onClick={install}>
              Install
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={dismiss}>
              Not now
            </Button>
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
